import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image, Plus, Trash2, Edit, Eye, EyeOff, GripVertical, RefreshCw, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import AdminLayout from './AdminLayout';
import { getGalleryItems, createGalleryItem, updateGalleryItem, deleteGalleryItem, isAuthenticated } from '../../services/adminApi';

const AdminGallery = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ title: '', imageUrl: '', sortOrder: 0, isPublished: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchItems();
  }, [navigate]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getGalleryItems();
      setItems(data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.imageUrl) return;
    setSaving(true);
    try {
      await createGalleryItem(formData);
      await fetchItems();
      setShowAddDialog(false);
      setFormData({ title: '', imageUrl: '', sortOrder: 0, isPublished: true });
    } catch (error) {
      console.error('Failed to create item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await updateGalleryItem(editingItem.id, formData);
      await fetchItems();
      setEditingItem(null);
      setFormData({ title: '', imageUrl: '', sortOrder: 0, isPublished: true });
    } catch (error) {
      console.error('Failed to update item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await deleteGalleryItem(itemId);
      await fetchItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      await updateGalleryItem(item.id, { isPublished: !item.isPublished });
      await fetchItems();
    } catch (error) {
      console.error('Failed to toggle publish:', error);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      imageUrl: item.imageUrl,
      sortOrder: item.sortOrder,
      isPublished: item.isPublished,
    });
  };

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-white">Gallery</h1>
            <p className="text-stone-400 mt-1">Manage gallery images (URL-only for now)</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={fetchItems}
              variant="outline"
              className="border-stone-600 text-stone-300 hover:bg-stone-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => {
                setFormData({ title: '', imageUrl: '', sortOrder: items.length, isPublished: true });
                setShowAddDialog(true);
              }}
              className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Image
            </Button>
          </div>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-stone-500 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <Card className="bg-[#2a2a2e] border-stone-700">
            <CardContent className="py-12 text-center">
              <Image className="w-12 h-12 text-stone-600 mx-auto mb-4" />
              <p className="text-stone-400 mb-4">No gallery images yet</p>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Image
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item) => (
              <Card key={item.id} className="bg-[#2a2a2e] border-stone-700 overflow-hidden group">
                <div className="aspect-square relative">
                  <img
                    src={item.imageUrl}
                    alt={item.title || 'Gallery image'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23666" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"/%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"/%3E%3Cpath d="M21 15l-5-5L5 21"/%3E%3C/svg%3E';
                    }}
                  />
                  {!item.isPublished && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <EyeOff className="w-8 h-8 text-white/60" />
                    </div>
                  )}
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(item)}
                      className="text-white hover:bg-white/20"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleTogglePublish(item)}
                      className="text-white hover:bg-white/20"
                    >
                      {item.isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-white text-sm truncate">{item.title || 'Untitled'}</p>
                    <span className="text-stone-500 text-xs">#{item.sortOrder}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-[#2a2a2e] border-stone-700">
            <DialogHeader>
              <DialogTitle className="text-white">Add Gallery Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-stone-300">Image URL *</Label>
                <Input
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-stone-300">Title (optional)</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Image title"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-stone-300">Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="bg-stone-800 border-stone-600 text-white mt-1 w-24"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.isPublished}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
                />
                <Label className="text-stone-300">Published</Label>
              </div>
              {formData.imageUrl && (
                <div className="mt-4">
                  <Label className="text-stone-300">Preview</Label>
                  <div className="mt-2 aspect-video bg-stone-800 rounded-lg overflow-hidden">
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-stone-600 text-stone-300">
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                disabled={!formData.imageUrl || saving}
                className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0"
              >
                {saving ? 'Adding...' : 'Add Image'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent className="bg-[#2a2a2e] border-stone-700">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Gallery Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-stone-300">Image URL *</Label>
                <Input
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-stone-300">Title (optional)</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Image title"
                  className="bg-stone-800 border-stone-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-stone-300">Sort Order</Label>
                <Input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="bg-stone-800 border-stone-600 text-white mt-1 w-24"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.isPublished}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
                />
                <Label className="text-stone-300">Published</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingItem(null)} className="border-stone-600 text-stone-300">
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={!formData.imageUrl || saving}
                className="bg-[#F5BCB1] hover:bg-[#efaaa0] text-stone-900 border-0"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminGallery;
